import React from "react";
import { ROLE_LABELS, STAFF_ROLES } from "../../../constants/roles";

type Props = {
  /** Rôle principal du compte édité : jamais proposé en rôle additionnel. */
  principalRole?: string | null;
  /** Rôles additionnels actuellement cochés. */
  value?: string[];
  onChange: (additionalRoles: string[]) => void;
  disabled?: boolean;
};

/**
 * Groupe de cases « Rôles additionnels » des écrans d'administration
 * (annuaire des utilisateurs, personnel médecin). Seuls les rôles du
 * personnel sont cumulables, et jamais le rôle principal lui-même : le
 * serveur applique la même règle et répond 400 sinon.
 *
 * L'appelant décide de la visibilité (réservée à l'administrateur) et
 * conserve la source de vérité du formulaire — ce composant n'a pas
 * d'état interne.
 */
const AdditionalRolesField = ({
  principalRole,
  value = [],
  onChange,
  disabled = false,
}: Props) => {
  const selectableRoles = STAFF_ROLES.filter(
    (role: string) => role !== principalRole
  );

  const toggleRole = (role: string) => {
    const nextRoles = value.includes(role)
      ? value.filter((selectedRole) => selectedRole !== role)
      : [...value, role];
    onChange(nextRoles);
  };

  return (
    <fieldset className="block" disabled={disabled}>
      <legend className="block text-sm font-medium text-slate-700">
        Rôles additionnels
      </legend>
      <p className="mt-0.5 text-xs text-slate-400">
        Droits cumulés au rôle principal. Facultatif.
      </p>
      <div className="mt-2 flex flex-wrap gap-2">
        {selectableRoles.map((role: string) => {
          const isSelected = value.includes(role);
          return (
            <label
              key={role}
              className={
                "inline-flex items-center gap-2 rounded-xl border px-3 py-2 text-sm transition-colors " +
                (disabled
                  ? "cursor-not-allowed border-slate-200 bg-slate-50 text-slate-400"
                  : "cursor-pointer " +
                    (isSelected
                      ? "border-emerald-500 bg-emerald-50 text-emerald-700 font-medium"
                      : "border-slate-200 bg-white text-slate-600 hover:border-slate-300"))
              }
            >
              <input
                type="checkbox"
                checked={isSelected}
                disabled={disabled}
                onChange={() => toggleRole(role)}
                className="h-4 w-4 rounded border-slate-300 accent-emerald-600 focus:ring-4 focus:ring-emerald-500/30"
              />
              {ROLE_LABELS[role as keyof typeof ROLE_LABELS] ?? role}
            </label>
          );
        })}
      </div>
    </fieldset>
  );
};

export default AdditionalRolesField;
